import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../helpers'
import { LeaseAuditSchedule, LeaseAuditScheduleStatus } from '../../models'
import { toast } from 'react-toastify'

// Helper function to get the event list query key
const getEventListQueryKey = (organizationId: number) =>
    ['audit-events', organizationId] as const

export const useAuditSchedules = (organizationId: number) => {
    const queryClient = useQueryClient()
    const auditListQueryKey = ['audit-schedules', organizationId] as const
    const eventListQueryKey = getEventListQueryKey(organizationId)

    const {
        data: leaseAuditSchedules,
        isLoading: isLoadingSchedules,
        error: errorSchedules,
    } = useQuery<LeaseAuditSchedule[]>({
        queryKey: auditListQueryKey,
        queryFn: async () => {
            const res = await axiosInstance.get<LeaseAuditSchedule[]>(
                '/document-audit/schedule/',
                {
                    params: {
                        organization_id: organizationId,
                    },
                },
            )
            return res.data
        },
    })

    const createSchedule = useMutation({
        mutationFn: async ({
            name,
            description,
            startDate,
            companies,
            recurrencePattern,
        }: {
            name: string
            description: string
            startDate: Date
            companies: number[]
            recurrencePattern: string
        }) => {
            const res = await axiosInstance.post<LeaseAuditSchedule>(
                'document-audit/schedule/',
                {
                    name,
                    description,
                    start_date: startDate,
                    companies,
                    recurrence_pattern: recurrencePattern,
                    organization_id: organizationId,
                },
            )
            return res.data
        },
        onSuccess: (newSchedule) => {
            queryClient.setQueryData<LeaseAuditSchedule[]>(
                auditListQueryKey,
                (old) => (old ? [...old, newSchedule] : [newSchedule]),
            )
            queryClient.invalidateQueries({
                queryKey: eventListQueryKey,
            })
            toast.success('Organization schedule created')
        },
        onError: () => {
            toast.error('Error creating organization schedule')
        },
    })

    const updateScheduleStatus = useMutation({
        mutationFn: async ({
            scheduleId,
            status,
        }: {
            scheduleId: number
            status: LeaseAuditScheduleStatus
        }) => {
            const res = await axiosInstance.patch<LeaseAuditSchedule>(
                `document-audit/schedule/${scheduleId}/`,
                {
                    status,
                },
            )
            return res.data
        },
        onSuccess: (updatedSchedule) => {
            queryClient.setQueryData<LeaseAuditSchedule[]>(
                auditListQueryKey,
                (old) =>
                    old?.map((schedule) =>
                        schedule.id === updatedSchedule.id
                            ? updatedSchedule
                            : schedule,
                    ),
            )
            queryClient.invalidateQueries({
                queryKey: eventListQueryKey,
            })
            toast.success('Schedule status updated successfully')
        },
        onError: () => {
            toast.error('Error updating schedule status')
        },
    })

    return {
        leaseAuditSchedules,
        isLoadingSchedules,
        errorSchedules,
        createSchedule,
        updateScheduleStatus,
    }
}
